import { TitleSpan } from '@/components/features/shared/TitleSpan'
import { DebtLabel, PriceLabel } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardContent, CardDescription, CardFooter, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatAgreementStatus, getAgreementStatusColor } from '@/domain/agreements/agreement'
import { getCollectorFullName } from '@/domain/collectors/collector'
import { PaymentData, PaymentStatus } from '@/domain/payments/payments'
import { useSellers } from '@/hooks/api/useSellers'
import { convertToDigitStr } from '@/utils/helpers'
import { CreditCard, DollarSign, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { InfoCard } from '../sub-components/InfoCard'

interface ModifyAgreementModalProps {
  isOpen: boolean
  onClose: () => void
  data: PaymentData | null
  isPending: boolean
  onModify: (amount: number) => void
}

export const ModifyAgreementModal = ({
  isOpen,
  onClose,
  data,
  isPending,
  onModify,
}: ModifyAgreementModalProps) => {
  const { data: sellers = [] } = useSellers()
  const [newAmount, setNewAmount] = useState<number>(0)

  const contractAmount = data?.contractAmount ?? 0
  const amountPaid = data?.payments.filter(p => p.status === PaymentStatus.COMPLETED || p.status === PaymentStatus.IN_COLLECTOR)
    .reduce((sum, p) => sum + p.amount, 0) ?? 0

  const remainingBalance = useMemo(() => {
    if (!data?.contractId) return 0
    return data.contractAmount - amountPaid
  }, [data, amountPaid])

  useEffect(() => {
    if (data) {
      setNewAmount(data.contractAmount)
    }
  }, [data])

  const handleConfirm = () => {
    if (newAmount <= 0) return
    onModify(newAmount)
  }

  if (!data) {
    return null
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex flex-col items-start justify-between">
            <CardTitle className='text-xl'>Modificar Monto del Contrato </CardTitle>
            <CardDescription>
              <h3 className="text-md font-bold flex items-center gap-2">
                {data.offeringCode} - {data.offeringName}
              </h3>
            </CardDescription>
          </div>
        </DialogHeader>

        <CardContent className="space-y-6">
          <InfoCard
            rows={[
              [
                <div key="total" className="space-y-1">
                  <TitleSpan>Monto Total</TitleSpan>
                  <PriceLabel price={contractAmount} />
                </div>,
                <div key="paid" className="space-y-1">
                  <TitleSpan>Monto Abonado</TitleSpan>
                  <PriceLabel price={amountPaid} />
                </div>,
                <div key="remaining" className="space-y-1">
                  <TitleSpan>Saldo Pendiente</TitleSpan>
                  {remainingBalance > 0 ? <DebtLabel amount={remainingBalance} /> : <PriceLabel price={0} />}
                </div>,
              ], [
                <div key="status" className="flex flex-col justify-center gap-1">
                  <TitleSpan>Estado</TitleSpan>
                  <Badge className={"w-fit " + getAgreementStatusColor(data?.status)}>
                    {formatAgreementStatus(data?.status)}
                  </Badge>
                </div>,
                <div key="seller" className="flex flex-col justify-center gap-1">
                  <TitleSpan>Vendedor</TitleSpan>
                  <span className="text-xs">{getCollectorFullName(sellers.find(s => s.id === data.sellerId))}</span>
                </div>
              ],
            ]}
          />

          <div className="p-4 border rounded-xl bg-muted/20">
            <Label>Nuevo monto del contrato</Label>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <Input
                type="number"
                value={newAmount || ''}
                onChange={e => setNewAmount(Number(e.target.value))}
                disabled={isPending}
                min={0}
              />
              <div className="flex items-center gap-2 px-3 py-2 bg-background rounded-lg border border-border min-w-[180px] justify-end">
                <DollarSign className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-xl font-bold font-mono text-foreground tabular-nums">
                  {convertToDigitStr(newAmount.toString())}
                </span>
              </div>
            </div>
          </div>

          <AlertWarning>Modificar el monto del contrato no afectará los pagos ya realizados. El monto nuevo debe ser mayor al monto total abonado hasta el momento.</AlertWarning>
        </CardContent>

        <CardFooter className="flex justify-between pt-0">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            <X className="h-3.5 w-3.5 mr-1.5" />
            Cancelar
          </Button>
          <Button
            variant="info"
            onClick={handleConfirm}
            disabled={newAmount <= 0 || isPending}
          >
            <CreditCard className="h-3.5 w-3.5 mr-1" />
            Modificar monto
          </Button>
        </CardFooter>
      </DialogContent>
    </Dialog>
  )
}
